import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type IntegrationOrgType } from '@/api/integration';
import { Button } from '@/components/buttons';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { Checkbox } from '@/components/Form';

type Props = {
  orgs?: IntegrationOrgType[];
  isLoading?: boolean;
  onCancel: () => void;
  onOk: (orgId: string) => void;
};

const SelectIntegrationOrgDialog = ({ orgs, isLoading = false, onCancel, onOk }: Props) => {
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>();

  const columns = useMemo<DataTableColumnDef<IntegrationOrgType>[]>(
    () => [
      {
        header: '',
        id: 'select',
        size: 40,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="pointer-events-none inline-flex">
            <Checkbox checked={row.original.id === selectedOrgId} />
          </span>
        ),
      },
      {
        header: t`Account`,
        id: 'name',
        minSize: 120,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.name,
      },
      {
        header: t`Account ID`,
        id: 'accountId',
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.id,
      },
    ],
    [selectedOrgId],
  );

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel();
    }
  };

  const handleConnect = () => {
    if (selectedOrgId) {
      onOk(selectedOrgId);
    }
  };

  return (
    <Dialog open={orgs != null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Choose an Account</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Your login has access to multiple accounts. Select the one you want to connect with
              Channel99.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5 text-center">
          <DataTable<IntegrationOrgType>
            className="h-71 overflow-y-auto"
            columns={columns}
            data={orgs ?? []}
            loading={false}
            error={null}
            emptyMessage={<Trans>No accounts exist</Trans>}
            pagination={false}
            getRowId={(record) => record.id}
            onRow={(record) => ({ onClick: () => setSelectedOrgId(record.id) })}
          />
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large">
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <Button
            size="large"
            variant="on-primary"
            isLoading={isLoading}
            disabled={selectedOrgId == null}
            onClick={handleConnect}
          >
            <Trans>Connect</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectIntegrationOrgDialog;
